import { isDefined, isNumber, isObject, isString, isUndefined } from './../utils';
import { randomId } from './randomId';
/**
 * Represents a collection of key-value pairs that are organized based on the hash code of the key.
 * _buckets[hashId] = {key: key, value:...}
 * Important: do not use the standard Array access method, use the get/set methods instead.
 * See http://en.wikipedia.org/wiki/Hash_table
 */
export class HashTable {
    constructor() {
        this._buckets = {};
        this.length = 0;
        this._stringsMap = new Map();
        this._stringsCounter = 0;
    }
    /**
     * Adds the literal object with the given key (of the form {key: key,....}).
     */
    add(key, value) {
        const obj = this._createGetBucket(key);
        if (isDefined(value)) {
            obj.value = value;
        }
        return obj;
    }
    /**
     * Gets the literal object with the given key.
     */
    get(key) {
        if (this._bucketExists(key)) {
            return this._createGetBucket(key);
        }
        return null;
    }
    /**
     * Set the key-value pair.
     *
     * @param key The key of the entry.
     * @param value The value to set. If the key already exists the value will be overwritten.
     */
    set(key, value) {
        this.add(key, value);
    }
    /**
     * Determines whether the HashTable contains a specific key.
     */
    containsKey(key) {
        return this._bucketExists(key);
    }
    /**
     * Removes the element with the specified key from the hashtable.
     * Returns the removed bucket.
     */
    remove(key) {
        if (this._bucketExists(key)) {
            const hashId = this._hash(key);
            delete this._buckets[hashId];
            this.length--;
            return key;
        }
    }
    /**
     * Foreach with an iterator working on the key-value pairs.
     *
     * @param func
     */
    forEach(func) {
        const hashes = this._hashes();
        for (let i = 0, len = hashes.length; i < len; i++) {
            const hash = hashes[i];
            const bucket = this._buckets[hash];
            if (isUndefined(bucket)) {
                continue;
            }
            func(bucket);
        }
    }
    /**
     * Returns a (shallow) clone of the current HashTable.
     *
     * @returns {HashTable}
     */
    clone() {
        const ht = new HashTable();
        const hashes = this._hashes();
        for (let i = 0, len = hashes.length; i < len; i++) {
            const hash = hashes[i];
            const bucket = this._buckets[hash];
            if (isUndefined(bucket)) {
                continue;
            }
            ht.add(bucket.key, bucket.value);
        }
        return ht;
    }
    /**
     * Returns the hashes of the buckets.
     *
     * @returns {Array}
     * @private
     */
    _hashes() {
        const hashes = [];
        for (const hash in this._buckets) {
            if (Object.prototype.hasOwnProperty.call(this._buckets, hash)) {
                hashes.push(hash);
            }
        }
        return hashes;
    }
    _bucketExists(key) {
        const hashId = this._hash(key);
        return isDefined(this._buckets[hashId]);
    }
    /**
     * Returns-adds the createGetBucket with the given key. If not present it will
     * be created and returned.
     * A createGetBucket is a literal object of the form {key: key, ...}.
     */
    _createGetBucket(key) {
        const hashId = this._hash(key);
        let bucket = this._buckets[hashId];
        if (isUndefined(bucket)) {
            bucket = { key: key };
            this._buckets[hashId] = bucket;
            this.length++;
        }
        return bucket;
    }
    /**
     * Hashing of the given key.
     */
    _hash(key) {
        if (isString(key) || isNumber(key)) {
            return this._hashString(String(key));
        }
        if (isObject(key)) {
            return this._objectHashId(key);
        }
        throw new Error('Unsupported key type.');
    }
    /**
     * Hashing of a string.
     */
    _hashString(s) {
        let result = 0;
        if (s.length === 0) {
            return result;
        }
        if (this._stringsMap.has(s)) {
            return this._stringsMap.get(s);
        }
        this._stringsCounter++;
        this._stringsMap.set(s, this._stringsCounter);
        result = this._stringsCounter;
        return result;
    }
    /**
     * Returns the unique identifier for an object. This is automatically assigned and add on the object.
     */
    _objectHashId(key) {
        let id = key._hashId;
        if (isUndefined(id)) {
            id = randomId();
            key._hashId = id;
        }
        return id;
    }
}
